import { useCallback, useLayoutEffect, useRef } from "react";

// Hold only an accepted reader selection, not week changes or other routes.
// ResizeObserver covers media/async sections after the keyed card mounts.
// User input releases the hold immediately; shorter content clamps naturally.
export function useReaderSelectionScroll(rootRef, selection, week) {
  const pending = useRef(null);
  const stop = useRef(null);
  const capture = useCallback(() => {
    stop.current?.();
    const root = rootRef.current;
    const shell = root?.closest(".v2-content") || document.scrollingElement;
    const rail = root?.querySelector(".wk-rail");
    if (shell) pending.current = { shell, rail, top: shell.scrollTop, left: shell.scrollLeft, railTop: rail?.scrollTop || 0, week };
  }, [rootRef, week]);

  useLayoutEffect(() => {
    stop.current?.();
    const saved = pending.current;
    pending.current = null;
    if (!saved || saved.week !== week) return undefined;
    const { shell, rail, top, left, railTop } = saved;
    let frame;
    let active = true;
    const restore = () => {
      if (!active) return;
      shell.scrollTop = Math.min(top, Math.max(0, shell.scrollHeight - shell.clientHeight));
      shell.scrollLeft = left;
      if (rail?.isConnected) rail.scrollTop = railTop;
    };
    const content = rootRef.current?.querySelector('[data-testid="weekly-reader-content"]');
    const anchoring = content?.style.overflowAnchor;
    if (content) content.style.overflowAnchor = "none";
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(restore);
    if (content) observer?.observe(content);
    const events = ["wheel", "touchstart", "pointerdown", "keydown"];
    const release = () => {
      active = false;
      if (content) content.style.overflowAnchor = anchoring;
      observer?.disconnect();
      cancelAnimationFrame(frame);
      events.forEach(event => shell.removeEventListener(event, release, true));
      if (stop.current === release) stop.current = null;
    };
    events.forEach(event => shell.addEventListener(event, release, { capture: true, passive: true }));
    stop.current = release;
    restore();
    frame = requestAnimationFrame(restore);
    return release;
  }, [selection, week, rootRef]);

  return capture;
}
